<script setup>
import { ref, computed } from 'vue'
import { useNow } from '@vueuse/core'

const props = defineProps({
  events: {
    type: Array,
    required: true
  }
});

const now = useNow({ interval: 60 * 1000 });
const monthOffset = ref(0)
// const displayedDate = ref(new Date());
const displayedDate = computed(() => {
  const newDate = new Date(now.value)
  newDate.setMonth(newDate.getMonth() + monthOffset.value);
  return newDate
})

// const currentDate = computed(() => displayedDate.value);

const today = computed(() => {
  const d = new Date(now.value);
  d.setHours(0, 0, 0, 0);
  return d;
});

const currentMonthYear = computed(() => displayedDate.value.toLocaleDateString([], { month: 'long', year: 'numeric' }))

const prevMonth = () => {
  monthOffset.value--
};

const nextMonth = () => {
  monthOffset.value++
};

// Helper to check if a date is in the past
const isPast = (date) => {
    return date < today.value;
};

// Helper to check if an event has ended
const isEventPast = (event) => {
    const eventEnd = new Date(event.end || event.start);
    return eventEnd < today.value;
};

// Helper to check if two dates are the same day
const isSameDay = (d1, d2) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();

const isToday = (date) => isSameDay(date, today.value);

const isEventNextMonth = (event) => {
  const eventStart = new Date(event.start);
  const currentDisplayMonth = displayedDate.value.getMonth();
  const currentDisplayYear = displayedDate.value.getFullYear();
  const eventMonth = eventStart.getMonth();
  const eventYear = eventStart.getFullYear();
  if (eventYear > currentDisplayYear) return true
  if (eventYear === currentDisplayYear && eventMonth > currentDisplayMonth) return true;
  return false;
}

// 1. Create a flat array of all days visible in the calendar grid
const calendarGridDays = computed(() => {
  const year = displayedDate.value.getFullYear()
  const month = displayedDate.value.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);

  const days = [];
  // Start from the Sunday of the first week
  const startDate = new Date(firstDayOfMonth);
  startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());

  // End on the Saturday of the last week
  const endDate = new Date(lastDayOfMonth);
  if (lastDayOfMonth.getDay() !== 6) {
    endDate.setDate(endDate.getDate() + (6 - lastDayOfMonth.getDay()));
  }

  let dayIterator = new Date(startDate);
  while (dayIterator <= endDate) {
    days.push(new Date(dayIterator));
    dayIterator.setDate(dayIterator.getDate() + 1);
  }
  return days;
});

// 2. Chunk the flat array of days into weeks
const weeks = computed(() => {
  const chunks = [];
  for (let i = 0; i < calendarGridDays.value.length; i += 7) {
    chunks.push(calendarGridDays.value.slice(i, i + 7));
  }
  return chunks;
});

// 3. Process events to calculate their layout for rendering
const laidOutEvents = computed(() => {
  const layouts = [];
  weeks.value.forEach((week, weekIndex) => {
    const weekStart = week[0];
    const weekEnd = week[6];
    const weekLevels = []; // Tracks event placement to avoid overlaps

    const eventsInWeek = props.events
      .filter(event => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end || event.start);
        return eventStart <= weekEnd && eventEnd >= weekStart;
      })
      .sort((a, b) => new Date(a.start) - new Date(b.start));

    eventsInWeek.forEach(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end || event.start);

      // If an event ends at midnight, it's considered to end on the previous day for display purposes.
      let endDayForDisplay = new Date(eventEnd);
      if (eventEnd.getHours() === 0 && eventEnd.getMinutes() === 0 && eventEnd.getSeconds() === 0 && eventEnd.getMilliseconds() === 0) {
        if (eventEnd > eventStart) {
          endDayForDisplay.setDate(endDayForDisplay.getDate() - 1);
        }
      }

      const startDayIndex = eventStart < weekStart ? 0 : week.findIndex(day => isSameDay(day, eventStart));
      const endDayIndex = eventEnd > weekEnd ? 6 : week.findIndex(day => isSameDay(day, endDayForDisplay));

      const clampedStart = Math.max(startDayIndex, 0);
      const clampedEnd = endDayIndex === -1 ? 6 : Math.min(endDayIndex, 6);

      // Find the first available vertical level
      let level = 0;
      while (true) {
        if (!weekLevels[level]) {
          weekLevels[level] = [];
        }
        const collision = weekLevels[level].some(placed => clampedStart <= placed.end && clampedEnd >= placed.start);
        if (!collision) {
          break;
        }
        level++;
      }

      weekLevels[level].push({ start: clampedStart, end: clampedEnd });
      layouts.push({
        event,
        weekIndex,
        start: clampedStart,
        span: clampedEnd - clampedStart + 1,
        level,
        key: `${event.id}-${weekIndex}`
      });
    });
  });

  return layouts;
});

const getEventStyle = (layout) => {
  const weekHeightPercentage = 100 / weeks.value.length;
  const top = `calc(${layout.weekIndex * weekHeightPercentage}% + 1.75rem + ${layout.level * 1.5}rem)`;
  const left = `calc(${(layout.start / 7) * 100}%)`;
  const width = `calc(${(layout.span / 7) * 100}%)`;
  return {
    top,
    left,
    width,
  };
};

</script>

<template>
  <div class="w-full border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-black flex flex-col h-full">
    <div class="flex justify-between items-center p-2.5 bg-neutral-100 dark:bg-neutral-800 flex-shrink-0">
      <button @click="prevMonth" class="px-4 py-2 bg-neutral-200 dark:bg-neutral-700 rounded">&lt;</button>
      <h2 class="text-2xl font-semibold">{{ currentMonthYear }}</h2>
      <button @click="nextMonth" class="px-4 py-2 bg-neutral-200 dark:bg-neutral-700 rounded">&gt;</button>
    </div>

    <div class="grid grid-cols-7 flex-shrink-0">
      <div class="text-center p-1.5 bg-neutral-200 dark:bg-neutral-700 font-semibold" v-for="day in ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']" :key="day">{{ day }}</div>
    </div>

    <div class="relative flex-grow min-h-0">
      <!-- Background grid of day cells -->
      <div class="grid grid-cols-7 h-full" :style="{'grid-template-rows': `repeat(${weeks.length}, minmax(0, 1fr))`}">
        <div v-for="day in calendarGridDays" :key="day.toISOString()" 
             class="p-1.5 border-t border-l border-neutral-200 dark:border-neutral-700" 
             :class="{
               'opacity-50': isPast(day) && !isToday(day),
               'outline-4 outline outline-red-600 bg-red-100 dark:bg-red-950 dark:outline-red-700': isToday(day)
             }">
          <div v-if="isToday(day)">
            <div class="font-bold text-red-800 dark:text-red-100">{{ day.getDate() }}</div>
            <div class="-mt-8 text-center text-lg font-semibold text-red-800 dark:text-red-100">Today</div>
          </div>
          <span v-else class="text-sm" 
                :class="{
                  'text-neutral-400 dark:text-neutral-500': day.getMonth() !== displayedDate.getMonth(),
                  'line-through': isPast(day)
                }">
            {{ day.getDate() }}
          </span>
        </div>
      </div>

      <!-- Absolutely positioned event layer -->
      <div class="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div v-for="(layout, index) in laidOutEvents" :key="index" 
             class="absolute rounded-md p-1 mb-0.5 text-xs whitespace-nowrap overflow-hidden text-ellipsis pointer-events-auto"
             :class="[{
               'line-through': isEventPast(layout.event),
               'opacity-100': !isEventPast(layout.event) && !isEventNextMonth(layout.event),
               'opacity-50': isEventPast(layout.event) || isEventNextMonth(layout.event),
             }, layout.event?.class]"
             :style="getEventStyle(layout)">
          {{ layout.event.title }}
        </div>
      </div>
    </div>
  </div>
</template>